/**
 * Silent access-token refresh interceptor with single-flight pattern
 * Handles 401 responses by refreshing tokens and replaying failed requests
 */

import { ENV } from '../config/env'
import { browserStorage, STORAGE_KEYS, type StorageAdapter } from '../lib/storage'
import { browserLocation, type LocationAdapter } from '../lib/location'
import { browserScheduler, type Scheduler } from '../lib/scheduler'
import { systemClock } from '../lib/clock'

// Injectable storage adapter for testability
let storageAdapter: StorageAdapter = browserStorage

/**
 * Set the storage adapter. Call in tests with createMemoryStorage().
 */
export const setStorageAdapter = (adapter: StorageAdapter): void => {
    storageAdapter = adapter
}

/**
 * Reset the storage adapter to the default browserStorage.
 * Call in test cleanup.
 */
export const resetStorageAdapter = (): void => {
    storageAdapter = browserStorage
}

// Injectable location adapter for testability
let locationAdapter: LocationAdapter = browserLocation

/**
 * Set the location adapter. Call in tests with createTestLocation().
 */
export const setLocationAdapter = (adapter: LocationAdapter): void => {
    locationAdapter = adapter
}

/**
 * Reset the location adapter to the default browserLocation.
 * Call in test cleanup.
 */
export const resetLocationAdapter = (): void => {
    locationAdapter = browserLocation
}

// Global auth failure handler - will be set by AuthProvider
let globalAuthFailureHandler: (() => void) | null = null

export const setAuthFailureHandler = (handler: () => void) => {
    globalAuthFailureHandler = handler
}

// Router-aware redirect handler. When registered, replaces window.location.href
// assignment so in-app redirects soft-navigate instead of forcing a full reload
// (which tears down the SPA and flashes the browser's default background).
let globalRedirectHandler: ((path: string) => void) | null = null

export const setRedirectHandler = (handler: ((path: string) => void) | null) => {
    globalRedirectHandler = handler
}

const redirectTo = (path: string): void => {
    if (globalRedirectHandler) {
        globalRedirectHandler(path)
    } else {
        locationAdapter.setHref(path)
    }
}

const API_BASE_URL = ENV.API_BASE_URL

// Types for request replay
interface QueuedRequest<T = unknown> {
    resolve: (value: T) => void
    reject: (error: Error) => void
    originalRequest: () => Promise<T>
    retryCount: number
}

interface RefreshResult {
    success: boolean
    accessToken?: string
    refreshToken?: string
    error?: Error
    isAuthError?: boolean // true if refresh failed due to auth (invalid/expired refresh token)
}

// Single-flight refresh state
let refreshPromise: Promise<RefreshResult> | null = null
// Queue stores requests with different return types, so we use unknown
let requestQueue: QueuedRequest<unknown>[] = []

// Analytics/logging events (lightweight, no PII)
type AuthEvent =
    | 'auth_refresh_start'
    | 'auth_refresh_success'
    | 'auth_refresh_failed_auth'
    | 'auth_refresh_failed_transient'
    | 'auth_replay_success'
    | 'auth_replay_failed'

const logAuthEvent = (event: AuthEvent, details?: Record<string, unknown>) => {
    // Lightweight logging - no PII, consistent with repo practices
    console.debug(`[Auth] ${event}`, details ? JSON.stringify(details) : '')

    // Optional: Send to analytics service if available
    // analytics?.track(event, details)
}

/**
 * Refresh access token using refresh token
 * Single-flight: only one refresh runs at a time
 */
const refreshAccessToken = async (): Promise<RefreshResult> => {
    const refreshToken = storageAdapter.getItem(STORAGE_KEYS.REFRESH_TOKEN)

    if (!refreshToken) {
        logAuthEvent('auth_refresh_failed_auth', { reason: 'no_refresh_token' })
        return {
            success: false,
            error: new Error('No refresh token available'),
            isAuthError: true,
        }
    }

    try {
        logAuthEvent('auth_refresh_start')

        const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refresh_token: refreshToken }),
        })

        if (!response.ok) {
            // Check if this is an auth error (invalid/expired refresh token)
            const isAuthError = response.status === 401 || response.status === 403
            logAuthEvent(
                isAuthError ? 'auth_refresh_failed_auth' : 'auth_refresh_failed_transient',
                {
                    status: response.status,
                    reason: isAuthError ? 'invalid_refresh_token' : 'server_error',
                }
            )

            return {
                success: false,
                error: new Error(`Token refresh failed: ${response.status}`),
                isAuthError,
            }
        }

        interface TokenRefreshResponse {
            access_token?: string
            refresh_token?: string
            data?: {
                access_token?: string
                refresh_token?: string
            }
        }

        const responseData = (await response.json()) as
            TokenRefreshResponse | { data: TokenRefreshResponse }

        // Unwrap response envelope: {data, meta} -> data
        const data: TokenRefreshResponse =
            responseData && typeof responseData === 'object' && 'data' in responseData
                ? (responseData as { data: TokenRefreshResponse }).data
                : responseData

        // Validate access_token is present (required for authentication)
        if (!data.access_token) {
            logAuthEvent('auth_refresh_failed_transient', {
                reason: 'missing_access_token',
            })
            return {
                success: false,
                error: new Error('Token refresh response missing access_token'),
                isAuthError: false,
            }
        }

        // Store new tokens (honor rotation if used)
        storageAdapter.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.access_token)
        if (data.refresh_token) {
            storageAdapter.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refresh_token)
        }

        logAuthEvent('auth_refresh_success')

        return {
            success: true,
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
        }
    } catch (error) {
        logAuthEvent('auth_refresh_failed_transient', {
            reason: 'network_error',
            error: error instanceof Error ? error.message : 'Unknown error',
        })

        return {
            success: false,
            error: error instanceof Error ? error : new Error('Network error during refresh'),
            isAuthError: false,
        }
    }
}

/**
 * Handle 401 response with single-flight refresh and request replay
 */
const handleUnauthorized = async <T>(
    originalRequest: () => Promise<T>,
    retryCount = 0
): Promise<T> => {
    // Prevent infinite retry loops
    if (retryCount >= 1) {
        throw new Error('Request failed after token refresh - maximum retries exceeded')
    }

    // Start refresh if none is in progress
    if (!refreshPromise) {
        refreshPromise = refreshAccessToken()
    }

    // Wait for refresh result
    // Store in local variable to satisfy TypeScript's control flow analysis
    const promise = refreshPromise
    const refreshResult = await promise

    if (!refreshResult.success) {
        // Clear refresh promise so next 401 can try again
        refreshPromise = null

        if (refreshResult.isAuthError) {
            // Auth failure - call global handler if available
            if (globalAuthFailureHandler) {
                globalAuthFailureHandler()
            }
            throw new Error('Your session has expired. Please log in again.')
        } else {
            // Transient failure - surface error to user
            throw new Error(
                'Connection issue. Please check your internet connection and try again.'
            )
        }
    }

    // Clear refresh promise on success
    refreshPromise = null

    // Replay the failed request once
    try {
        logAuthEvent('auth_replay_success')
        return await originalRequest()
    } catch (error) {
        logAuthEvent('auth_replay_failed', {
            error: error instanceof Error ? error.message : 'Unknown error',
        })
        throw error
    }
}

/**
 * Queue management for concurrent requests during refresh
 */
const queueRequest = <T>(originalRequest: () => Promise<T>): Promise<T> => {
    return new Promise<T>((resolve, reject) => {
        const queuedRequest: QueuedRequest<T> = {
            resolve,
            reject: (error: Error) => reject(error),
            originalRequest,
            retryCount: 0,
        }

        requestQueue.push(queuedRequest as QueuedRequest<unknown>)

        // Process queue when refresh completes
        if (refreshPromise) {
            void refreshPromise.then((refreshResult) => {
                if (refreshResult.success) {
                    void processQueuedRequests()
                } else {
                    // Reject all queued requests with the same error
                    requestQueue.forEach((req) => {
                        if (refreshResult.isAuthError) {
                            req.reject(new Error('Your session has expired. Please log in again.'))
                        } else {
                            req.reject(
                                new Error(
                                    'Connection issue. Please check your internet connection and try again.'
                                )
                            )
                        }
                    })
                    requestQueue = []
                }
            })
        }
    })
}

/**
 * Process queued requests after successful refresh
 */
const processQueuedRequests = async () => {
    const requests = [...requestQueue]
    requestQueue = []

    for (const request of requests) {
        try {
            const result = await request.originalRequest()
            request.resolve(result)
        } catch (error) {
            request.reject(error instanceof Error ? error : new Error(String(error)))
        }
    }
}

/**
 * Enhanced fetch wrapper with automatic token refresh
 * Handles 401 responses by refreshing tokens and replaying requests
 * Proactively refreshes tokens before they expire to prevent 401 errors
 */
export const authenticatedFetch = async (
    url: string,
    options: RequestInit = {}
): Promise<Response> => {
    // Guard: Skip interceptor for refresh endpoint to prevent recursion
    if (url.includes('/api/v1/auth/refresh')) {
        return fetch(url, options)
    }

    // Proactive token refresh: check if token is expiring soon before making request
    let token = storageAdapter.getItem(STORAGE_KEYS.ACCESS_TOKEN)
    if (token && isTokenExpiringSoon(token)) {
        const refreshToken = storageAdapter.getItem(STORAGE_KEYS.REFRESH_TOKEN)
        if (refreshToken) {
            // Use single-flight refresh to avoid multiple concurrent refreshes
            if (!refreshPromise) {
                refreshPromise = refreshAccessToken()
            }

            // Wait for refresh to complete
            // Store in local variable to satisfy TypeScript's control flow analysis
            const promise = refreshPromise
            const refreshResult = await promise

            // Clear refresh promise after use (matches handleUnauthorized pattern)
            // All concurrent requests will have stored the promise locally,
            // so clearing refreshPromise here is safe even if other requests are still awaiting
            refreshPromise = null

            if (refreshResult.success && refreshResult.accessToken) {
                token = refreshResult.accessToken
                // Update token in storage so subsequent requests use the new token
                storageAdapter.setItem(STORAGE_KEYS.ACCESS_TOKEN, refreshResult.accessToken)
            }
            // If refresh fails, proceed with original token (401 fallback will handle it)
            // Note: The 401 handler will attempt refresh again, which is fine
        }
    }

    const externalSignal = options.signal

    // Add authorization header if token is present
    // Don't set Content-Type for FormData - browser will set it with boundary
    const isFormData = options.body instanceof FormData
    const config: RequestInit = {
        ...options,
        headers: {
            ...(!isFormData && { 'Content-Type': 'application/json' }),
            ...(token && { Authorization: `Bearer ${token}` }),
            ...options.headers,
        },
    }

    const makeRequest = async (): Promise<Response> => {
        // Add timeout to prevent hanging requests
        const controller = new AbortController()
        // Use scheduler abstraction for testability (defaults to browserScheduler)
        // Note: For full testability, scheduler could be injected, but for now we use the default
        const scheduler: Scheduler = browserScheduler
        const timeoutId = scheduler.setTimeout(() => controller.abort(), 120000) // 120 second timeout (2 minutes) to accommodate LLM API calls

        // If caller provided a signal, link it to our controller
        if (externalSignal) {
            if (externalSignal.aborted) {
                scheduler.clearTimeout(timeoutId)
                throw new DOMException('Aborted', 'AbortError')
            }
            externalSignal.addEventListener('abort', () => controller.abort(), { once: true })
        }

        try {
            const response = await fetch(url, {
                ...config,
                signal: controller.signal,
            })
            scheduler.clearTimeout(timeoutId)
            return response
        } catch (error) {
            scheduler.clearTimeout(timeoutId)
            if (error instanceof Error && error.name === 'AbortError') {
                throw new Error('Request timed out. Please check your connection and try again.', {
                    cause: error,
                })
            }
            throw error
        }
    }

    try {
        const response = await makeRequest()

        // If 401 and we have a refresh token, try to refresh and replay
        if (response.status === 401 && storageAdapter.getItem(STORAGE_KEYS.REFRESH_TOKEN)) {
            // Clear any stale refresh promise from a previous failed attempt
            // This ensures we always attempt a fresh refresh on 401, even if proactive refresh failed
            if (refreshPromise) {
                // If refresh is already in progress, queue this request to wait for it
                return queueRequest(makeRequest)
            }

            // Handle unauthorized with single-flight refresh
            // This will attempt to refresh the token and replay the request
            return await handleUnauthorized(makeRequest)
        }

        // Handle 412 LLM_KEY_REQUIRED - redirect to profile (except translate evaluate which surfaces inline)
        if (response.status === 412) {
            const errorData = (await response.json().catch(() => ({}))) as {
                error?: string
                code?: string
            }
            const isLLMKeyRequired =
                errorData.error === 'LLM_KEY_REQUIRED' || errorData.code === 'LLM_KEY_REQUIRED'
            const isTranslateEvaluate = url.includes('/api/v1/translate/evaluate')
            if (isLLMKeyRequired && !isTranslateEvaluate) {
                const returnTo = encodeURIComponent(
                    locationAdapter.getPathname() + locationAdapter.getSearch()
                )
                redirectTo(`/profile?returnTo=${returnTo}`)
                throw new Error('LLM key required - redirecting to profile')
            }
            // For translate evaluate or other 412s, return a Response with the same body for caller handling
            return new Response(JSON.stringify(errorData), {
                status: response.status,
                headers: { 'Content-Type': 'application/json' },
            })
        }

        // Handle 403 SUBSCRIPTION_INACTIVE - redirect to account-inactive page
        if (response.status === 403) {
            const errorData = (await response.json().catch(() => ({}))) as {
                error?: string
                code?: string
            }
            const isSubscriptionInactive =
                errorData.error === 'SUBSCRIPTION_INACTIVE' ||
                errorData.code === 'SUBSCRIPTION_INACTIVE'
            if (isSubscriptionInactive) {
                const currentPath = locationAdapter.getPathname()
                if (currentPath !== '/account-inactive') {
                    redirectTo('/account-inactive')
                }
                throw new Error('Subscription inactive - redirecting to account-inactive')
            }
            // For other 403s, return the response for caller handling
            return new Response(JSON.stringify(errorData), {
                status: response.status,
                headers: { 'Content-Type': 'application/json' },
            })
        }

        return response
    } catch (error: unknown) {
        // If it's an auth error, we need to redirect
        if (error instanceof Error && error.message.includes('Authentication failed')) {
            // This will be handled by the caller (AuthContext)
            throw error
        }
        throw error
    }
}

/**
 * Check if access token is expired or expiring soon (clock skew cushion)
 *
 * @param token - The JWT token to check
 * @param cushionMs - Time cushion in milliseconds (defaults to 60000ms / 1 minute)
 * @param now - Current time in milliseconds (defaults to clock.now()). Injectable for testing.
 */
export const isTokenExpiringSoon = (
    token: string,
    cushionMs = 60000,
    now: number = systemClock.now()
): boolean => {
    try {
        const payload = JSON.parse(atob(token.split('.')[1])) as { exp?: number }
        const exp = (payload.exp ?? 0) * 1000
        return exp - now < cushionMs
    } catch {
        return true // If we can't parse, treat as expired
    }
}

/**
 * Get current access token, refreshing if needed
 * Used for proactive token refresh
 */
export const getValidAccessToken = async (): Promise<string | null> => {
    const token = storageAdapter.getItem(STORAGE_KEYS.ACCESS_TOKEN)

    if (!token) {
        return null
    }

    // If token is expiring soon, refresh proactively
    if (isTokenExpiringSoon(token)) {
        const refreshToken = storageAdapter.getItem(STORAGE_KEYS.REFRESH_TOKEN)
        if (refreshToken) {
            // Use single-flight refresh to avoid multiple concurrent refreshes
            if (!refreshPromise) {
                refreshPromise = refreshAccessToken()
            }

            // Store in local variable to satisfy TypeScript's control flow analysis
            const promise = refreshPromise
            const refreshResult = await promise
            refreshPromise = null // Clear after use

            if (refreshResult.success && refreshResult.accessToken) {
                return refreshResult.accessToken
            }
        }
        return null
    }

    return token
}

/**
 * Clear refresh state (useful for logout)
 */
export const clearRefreshState = () => {
    refreshPromise = null
    requestQueue.forEach((req) => {
        req.reject(new Error('Authentication cleared'))
    })
    requestQueue = []
}

// Export types for use in other modules
export type { AuthEvent, RefreshResult, QueuedRequest }
